import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Autocomplete,
  Avatar,
  styled,
  Pagination,
  Stack
} from '@mui/material';
import {
  Description as DocumentIcon,
  Assignment as TaskIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  Clear as ClearIcon,
  List as ListIcon,
  ForwardToInbox as ForwardToInboxIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  GetApp as GetAppIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { generateDocumentsExcel, generateTasksExcel, generateDocumentsPDF, generateTasksPDF } from '../utils/limitedAdminReportPDF';
import { useAuth } from '../context/AuthContext';
import { limitedAdminAPI } from '../api/limitedAdminAPI';
import { apiClient } from '../api/apiClient';
import { RTLTextField, RTLFormControl, RTLAutocomplete } from '../styles/rtlComponents';
import useRTL from '../hooks/useRTL';

// Import task components
import TaskCard from '../components/tasks/TaskCard';
import TaskDetailDialog from '../components/tasks/TaskDetailDialogV2';
import { RejectDialog, ApproveDialog } from '../components/tasks/TaskDialogs';

// Import task handlers
import { 
  createTaskAcceptHandler, 
  createTaskRejectHandler, 
  createTaskApproveHandler
} from '../components/tasks/TaskHandlers';

// Import API hooks
import useUpdate from '../api/useUpdate';
import useAdd from '../api/useAdd';
import useFetchObjects from '../api/useFetchObjects';

// Using centralized RTL components from styles/rtlComponents.js

const LimitedAdminDashboard = () => {
  // SECURITY NOTE: This component enforces strict data access controls
  // Limited admins can ONLY view data for explicitly selected users
  // When no users are selected, NO data is shown to prevent data leakage
  
  const { t, i18n } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { isRTL, getRTLProps } = useRTL();
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [systemOverview, setSystemOverview] = useState(null);
  const [allUsers, setAllUsers] = useState(null);
  const [allDocuments, setAllDocuments] = useState(null);
  const [allTaskAssignments, setAllTaskAssignments] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showAllUsers, setShowAllUsers] = useState(true);
  const [taskViewMode, setTaskViewMode] = useState('all-related'); // 'assigned-to-me', 'assigned-by-me', or 'all-related'
  const [selectedStatus, setSelectedStatus] = useState('all'); // 'all', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED'
  
  // Search and filter states
  const [documentSearch, setDocumentSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [documentStatusFilter, setDocumentStatusFilter] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');
  const [taskStatusFilter, setTaskStatusFilter] = useState('');
  
  // Pagination states
  const [documentPage, setDocumentPage] = useState(1);
  const [taskPage, setTaskPage] = useState(1);
  const [documentPageSize, setDocumentPageSize] = useState(50);
  const [taskPageSize, setTaskPageSize] = useState(50);
  const [documentTotalCount, setDocumentTotalCount] = useState(0);
  const [taskTotalCount, setTaskTotalCount] = useState(0);
  
  // Task dialog states
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  // Limited admins see data for their assigned users only
  const shouldLoadData = true; // Always load data for assigned users
  
  // API hooks for task operations
  const { handleUpdate: updateTask } = useUpdate('task-assignments', user?.token, null, null, null, false);
  const { handleAdd: createActivity } = useAdd('document-activities', user?.token, null, null, null, false);
  
  // Task handlers
  const handleTaskAccept = createTaskAcceptHandler(
    updateTask,
    createActivity,
    async () => Promise.resolve(), // updateWorkflowStep stub
    user,
    t,
    () => {
      // Refresh tasks after action
      if (allTaskAssignments?.data?.taskAssignments) {
        fetchAssignedUsers();
      }
    },
    setTaskDetailOpen
  );
  
  const handleTaskReject = createTaskRejectHandler(
    updateTask,
    createActivity,
    async () => Promise.resolve(), // updateWorkflowStep stub
    user,
    t,
    () => {
      // Refresh tasks after action
      if (allTaskAssignments?.data?.taskAssignments) {
        fetchAssignedUsers();
      }
    },
    setRejectDialogOpen
  );
  
  const handleTaskApprove = createTaskApproveHandler(
    updateTask,
    createActivity,
    async () => Promise.resolve(), // updateWorkflowStep stub
    async () => Promise.resolve(), // updateWorkflowApi stub
    user,
    t,
    () => {
      // Refresh tasks after action
      if (allTaskAssignments?.data?.taskAssignments) {
        fetchAssignedUsers();
      }
    },
    setApproveDialogOpen
  );

  // Load assigned users only on component mount
  useEffect(() => {
    fetchAssignedUsers();
  }, [user]);

  // RTL detection
  // RTL state is now managed by useRTL hook

  // Auto-switch to "all-related" mode when users are selected and load their data
  useEffect(() => {
    if (selectedUserIds.length > 0) {
      console.log('Users selected, auto-switching to all-related mode and loading data');
      console.log('Selected user IDs:', selectedUserIds);
      if (taskViewMode !== 'all-related') {
        setTaskViewMode('all-related');
      }
      // Load data for selected users with force refresh
      fetchUserData(selectedUserIds);
    } else {
      console.log('No users selected - clearing data');
      setAllDocuments({ data: { documents: [] } });
      setAllTaskAssignments({ data: { taskAssignments: [] } });
    }
  }, [selectedUserIds]);

  // Reset status filter when view mode changes
  useEffect(() => {
    setSelectedStatus('all');
    setTaskStatusFilter('');
  }, [taskViewMode]);

  // Refetch documents when document pagination changes
  useEffect(() => {
    if (selectedUserIds.length > 0 && activeTab === 0) {
      console.log('Document pagination changed - fetching documents');
      fetchDocuments(selectedUserIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentPage, documentPageSize]);

  // Refetch tasks when task pagination changes
  useEffect(() => {
    if (selectedUserIds.length > 0 && activeTab === 1) {
      console.log('Task pagination changed - fetching tasks');
      fetchTasks(selectedUserIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskPage, taskPageSize]);

  // Fetch task assignments for documents on current page (similar to DocumentList)
  const documentIds = allDocuments?.data?.documents?.map(doc => doc.id).join(',') || '';
  console.log('Document IDs for task assignments:', documentIds);
  const { data: taskAssignmentsForDocuments } = useFetchObjects(
    'task-assignments/by-document-ids',
    { document_ids: documentIds },
    user?.token,
    { enabled: Boolean(documentIds && activeTab === 0 && selectedUserIds.length > 0) }
  );

  console.log('Task assignments for documents:', taskAssignmentsForDocuments);

  // Merge task assignments from both sources
  useEffect(() => {
    if (taskAssignmentsForDocuments && Array.isArray(taskAssignmentsForDocuments) && activeTab === 0) {
      console.log('Merging task assignments for documents tab');
      // Merge with existing task assignments
      setAllTaskAssignments(prevState => {
        const existingTasks = prevState?.data?.taskAssignments || [];
        const documentTasks = taskAssignmentsForDocuments;
        
        console.log('Existing tasks:', existingTasks.length);
        console.log('Document tasks:', documentTasks.length);
        
        // Create a map grouped by document_id to get the latest assignment for each document
        const taskMap = new Map();
        [...existingTasks, ...documentTasks].forEach(task => {
          const docId = task.document_id;
          const existing = taskMap.get(docId);
          
          // If no existing task for this document, or this task is more recent, use this one
          if (!existing || 
              !task.assigned_at || 
              !existing.assigned_at || 
              new Date(task.assigned_at) > new Date(existing.assigned_at)) {
            taskMap.set(docId, task);
          }
        });
        
        console.log('Merged tasks:', taskMap.size);
        
        // Update state with merged tasks
        return { data: { taskAssignments: Array.from(taskMap.values()) } };
      });
    }
  }, [taskAssignmentsForDocuments, activeTab]);

  // Handle status card click - integrate with filter
  const handleStatusClick = (status) => {
    console.log('Status card clicked:', status);
    setSelectedStatus(status);
    // Also update the taskStatusFilter when clicking status cards
    if (status !== 'all') {
      setTaskStatusFilter(status);
    } else {
      setTaskStatusFilter('');
    }
  };

  // Get status cards configuration based on view mode
  const getStatusCardsConfig = () => {
    console.log('=== getStatusCardsConfig Debug ===');
    console.log('taskViewMode:', taskViewMode);
    console.log('selectedUserIds:', selectedUserIds);
    console.log('allTaskAssignments:', allTaskAssignments);
    
    // Common status cards configuration for all modes
    return [
      {
        key: 'ASSIGNED',
        status: 'ASSIGNED',
        label: t('limitedadmin:assigned'),
        icon: TaskIcon,
        color: 'secondary',
        bgColor: 'secondary.100',
        getCount: () => getFilteredTasksByStatus('ASSIGNED').length
      },
      {
        key: 'IN_PROGRESS',
        status: 'IN_PROGRESS',
        label: t('limitedadmin:in_progress'),
        icon: PersonIcon,
        color: 'info',
        bgColor: 'info.100',
        getCount: () => getFilteredTasksByStatus('IN_PROGRESS').length + getFilteredTasksByStatus('ACCEPTED').length
      },
      {
        key: 'COMPLETED',
        status: 'COMPLETED',
        label: t('limitedadmin:completed'),
        icon: CheckCircleIcon,
        color: 'success',
        bgColor: 'success.100',
        getCount: () => getFilteredTasksByStatus('COMPLETED').length
      },
      {
        key: 'REJECTED',
        status: 'REJECTED',
        label: t('limitedadmin:rejected'),
        icon: ClearIcon,
        color: 'error',
        bgColor: 'error.100',
        getCount: () => getFilteredTasksByStatus('REJECTED').length
      },
      {
        key: 'FORWARDED',
        status: 'FORWARDED',
        label: t('limitedadmin:forwarded'),
        icon: ForwardToInboxIcon,
        color: 'warning',
        bgColor: 'warning.100',
        getCount: () => getFilteredTasksByStatus('FORWARDED').length
      },
      {
        key: 'all',
        status: 'all',
        label: t('limitedadmin:all_tasks'),
        icon: ListIcon,
        color: 'grey',
        bgColor: 'grey.100',
        getCount: () => getAllTasksForMode().length
      }
    ];
  };

  // Helper function to get tasks filtered by status (without the selectedStatus filter)
  const getFilteredTasksByStatus = (status) => {
    if (!allTaskAssignments?.data?.taskAssignments) return [];
    
    const allTasks = allTaskAssignments.data.taskAssignments;
    let filteredTasks = [];
    
    console.log(`=== getFilteredTasksByStatus(${status}) ===`);
    console.log('allTasks count:', allTasks.length);
    console.log('selectedUserIds:', selectedUserIds);
    console.log('taskViewMode:', taskViewMode);
    console.log('user?.id:', user?.id);
    
    // Filter by user selection and view mode
    if (selectedUserIds.length === 0) {
      // If no users selected, show no tasks
      filteredTasks = [];
      console.log('No users selected - showing no tasks');
    } else if (taskViewMode === 'assigned-by-me') {
      // Show tasks assigned BY the selected users (they are the assigners)
      filteredTasks = allTasks.filter(task => 
        selectedUserIds.includes(task.assigner_id)
      );
      console.log('assigned-by-me filtered tasks:', filteredTasks.length);
    } else if (taskViewMode === 'assigned-to-me') {
      // Show tasks assigned TO selected users (they are the assignees)
      filteredTasks = allTasks.filter(task => 
        selectedUserIds.includes(task.assignee_id)
      );
      console.log('assigned-to-me filtered tasks:', filteredTasks.length);
    } else if (taskViewMode === 'all-related') {
      // Show ALL tasks related to selected users (both assigned to them and assigned by them)
      filteredTasks = allTasks.filter(task => 
        selectedUserIds.includes(task.assignee_id) || selectedUserIds.includes(task.assigner_id)
      );
      console.log('all-related filtered tasks:', filteredTasks.length);
    }
    
    // Apply ownership-based filtering for specific statuses
    if (status === 'COMPLETED') {
      // Completed tasks should only be shown to the original assigner (owner)
      const result = filteredTasks.filter(task => 
        task.status === 'COMPLETED' && selectedUserIds.includes(task.assigner_id)
      );
      console.log(`COMPLETED tasks (only to assigner):`, result.length);
      return result;
    } else if (status === 'FORWARDED') {
      // Forwarded tasks should only be shown to the forwarder
      const result = filteredTasks.filter(task => 
        task.status === 'FORWARDED' && selectedUserIds.includes(task.forwarded_by_id)
      );
      console.log(`FORWARDED tasks (only to forwarder):`, result.length);
      return result;
    } else if (status === 'IN_PROGRESS') {
      const result = filteredTasks.filter(task => 
        task.status === 'IN_PROGRESS' || task.status === 'ACCEPTED'
      );
      console.log(`IN_PROGRESS + ACCEPTED tasks:`, result.length);
      return result;
    } else if (status === 'all') {
      // For 'all' status, apply ownership rules to the base filtered tasks
      const result = filteredTasks.filter(task => {
        // Apply ownership rules for specific statuses
        if (task.status === 'COMPLETED') {
          return selectedUserIds.includes(task.assigner_id);
        } else if (task.status === 'FORWARDED') {
          return selectedUserIds.includes(task.forwarded_by_id);
        }
        // For other statuses, use normal filtering
        return true;
      });
      console.log(`All tasks with ownership rules:`, result.length);
      return result;
    } else {
      const result = filteredTasks.filter(task => task.status === status);
      console.log(`${status} tasks:`, result.length);
      return result;
    }
  };

  // Helper function to get all tasks for current mode
  const getAllTasksForMode = () => {
    if (!allTaskAssignments?.data?.taskAssignments) return [];
    
    const allTasks = allTaskAssignments.data.taskAssignments;
    
    // Filter by user selection and view mode
    if (selectedUserIds.length === 0) {
      // If no users selected, show no tasks
      return [];
    } else if (taskViewMode === 'assigned-by-me') {
      console.log('=== getAllTasksForMode ASSIGNED-BY-ME DEBUG ===');
      console.log('Current user ID:', user?.id);
      console.log('Selected user IDs:', selectedUserIds);
      console.log('All tasks count:', allTasks.length);
      
      // For assigned-by-me mode, show tasks assigned BY the selected users
      const baseFiltered = allTasks.filter(task => 
        selectedUserIds.includes(task.assigner_id)
      );
      console.log('Base filtered tasks (before ownership rules):', baseFiltered.length);
      
      // Apply ownership rules
      const finalFiltered = baseFiltered.filter(task => {
        if (task.status === 'COMPLETED') {
          return selectedUserIds.includes(task.assigner_id);
        } else if (task.status === 'FORWARDED') {
          return selectedUserIds.includes(task.forwarded_by_id);
        }
        return true;
      });
      console.log('Final filtered tasks (after ownership rules):', finalFiltered.length);
      return finalFiltered;
    } else if (taskViewMode === 'assigned-to-me') {
      const baseFiltered = allTasks.filter(task => 
        selectedUserIds.includes(task.assignee_id)
      );
      // Apply ownership rules
      return baseFiltered.filter(task => {
        if (task.status === 'COMPLETED') {
          return selectedUserIds.includes(task.assigner_id);
        } else if (task.status === 'FORWARDED') {
          return selectedUserIds.includes(task.forwarded_by_id);
        }
        return true;
      });
    } else if (taskViewMode === 'all-related') {
      // Show ALL tasks related to selected users (both assigned to them and assigned by them)
      const baseFiltered = allTasks.filter(task => 
        selectedUserIds.includes(task.assignee_id) || selectedUserIds.includes(task.assigner_id)
      );
      // Apply ownership rules
      return baseFiltered.filter(task => {
        if (task.status === 'COMPLETED') {
          return selectedUserIds.includes(task.assigner_id);
        } else if (task.status === 'FORWARDED') {
          return selectedUserIds.includes(task.forwarded_by_id);
        }
        return true;
      });
    }
    
    return [];
  };

  // Debug effect to log task data changes
  useEffect(() => {
    console.log('=== Task Data Debug ===');
    console.log('allTaskAssignments:', allTaskAssignments);
    console.log('selectedUserIds:', selectedUserIds);
    console.log('taskViewMode:', taskViewMode);
    console.log('getFilteredTasks():', getFilteredTasks());
  }, [allTaskAssignments, selectedUserIds, taskViewMode]);

  // SECURITY FIX: Removed auto-loading of all users' data
  // Limited admins should only see data for explicitly selected users
  useEffect(() => {
    if (allUsers?.data?.length > 0 && selectedUserIds.length === 0 && !showAllUsers) {
      console.log('SECURITY: Auto-loading disabled - no users selected, clearing data');
      // Clear all data instead of auto-loading all users' data
      setAllDocuments({ data: { documents: [] } });
      setAllTaskAssignments({ data: { taskAssignments: [] } });
      setDocumentTotalCount(0);
      setTaskTotalCount(0);
    }
  }, [allUsers, selectedUserIds, showAllUsers]);

  const fetchAssignedUsers = async () => {
    try {
      setDataLoading(true);
      setError(null);

      console.log('=== FETCHING ASSIGNED USERS ===');
      const assignedUsersData = await limitedAdminAPI.getAssignedUsers();
      console.log('Assigned users response:', assignedUsersData);
      
      const assignedUsers = assignedUsersData.data?.data || assignedUsersData.data || [];
      console.log('Setting allUsers with data:', { data: assignedUsers });
      setAllUsers({ data: assignedUsers });

      // Set system health
      setSystemHealth({
        status: 'healthy',
        score: 95
      });

    } catch (err) {
      console.error('Error fetching assigned users:', err);
      setError(err.message || 'Failed to load assigned users');
    } finally {
      setDataLoading(false);
    }
  };

  // Separate function to fetch documents only
  const fetchDocuments = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      setAllDocuments({ data: { documents: [] } });
      setDocumentTotalCount(0);
      return;
    }

    try {
      setDataLoading(true);
      setError(null);

      console.log('=== FETCHING DOCUMENTS FOR:', userIds);
      console.log('Document page:', documentPage, 'Page size:', documentPageSize);

      const userIdsParam = userIds.join(',');
      const timestamp = Date.now();
      
      const documentsData = await limitedAdminAPI.getDocuments({ 
        page: documentPage, 
        limit: documentPageSize, 
        user_id: userIdsParam, 
        _t: timestamp 
      });

      console.log('Documents response:', documentsData);

      const allDocuments = documentsData?.data?.data?.documents || [];
      const documentsTotalCount = documentsData?.data?.data?.totalCount || 0;
      
      console.log('Total documents from API:', allDocuments.length);
      console.log('Total documents available:', documentsTotalCount);

      setDocumentTotalCount(documentsTotalCount);
      setAllDocuments({ data: { documents: allDocuments } });

    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setDataLoading(false);
    }
  };

  // Separate function to fetch tasks only
  const fetchTasks = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      setAllTaskAssignments({ data: { taskAssignments: [] } });
      setTaskTotalCount(0);
      return;
    }

    try {
      setDataLoading(true);
      setError(null);

      console.log('=== FETCHING TASKS FOR:', userIds);
      console.log('Task page:', taskPage, 'Page size:', taskPageSize);

      const userIdsParam = userIds.join(',');
      const timestamp = Date.now();
      
      const tasksData = await limitedAdminAPI.getTaskAssignments({ 
        page: taskPage, 
        limit: taskPageSize, 
        user_id: userIdsParam, 
        _t: timestamp 
      });

      console.log('Tasks response:', tasksData);

      const allTasks = tasksData?.data?.data?.taskAssignments || [];
      const tasksTotalCount = tasksData?.data?.data?.totalCount || 0;
      
      console.log('Total tasks from API:', allTasks.length);
      console.log('Total tasks available:', tasksTotalCount);

      setTaskTotalCount(tasksTotalCount);
      setAllTaskAssignments({ data: { taskAssignments: allTasks } });

    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.message || 'Failed to load tasks');
    } finally {
      setDataLoading(false);
    }
  };

  // Combined function to fetch both documents and tasks (for initial load)
  const fetchUserData = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      setAllDocuments({ data: { documents: [] } });
      setAllTaskAssignments({ data: { taskAssignments: [] } });
      setSystemOverview({
        totalUsers: allUsers?.data?.length || 0,
        activeUsers: allUsers?.data?.filter(user => user.is_active).length || 0,
        totalDocuments: 0
      });
      setDocumentTotalCount(0);
      setTaskTotalCount(0);
      return;
    }

    await fetchDocuments(userIds);
    await fetchTasks(userIds);
    
    // Update overview
    setSystemOverview({
      totalUsers: allUsers?.data?.length || 0,
      activeUsers: allUsers?.data?.filter(user => user.is_active).length || 0,
      totalDocuments: documentTotalCount
    });
  };

  // Function to manually fetch user profile
  const fetchUserProfile = async () => {
    try {
      console.log('=== FETCHING USER PROFILE ===');
      const response = await apiClient.get(`/users/${user.id}`);
      console.log('User profile response:', response.data);
      // Update user in context
      login({ user: response.data, token: user.token });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset search and filters when switching tabs
    setDocumentSearch('');
    setTaskSearch('');
    setDocumentStatusFilter('');
    setDocumentTypeFilter('');
    setTaskStatusFilter('');
    setShowFilters(false);
    // Reset pagination when switching tabs
    setDocumentPage(1);
    setTaskPage(1);
  };


  const handleUserSelection = (userIds) => {
    console.log('=== USER SELECTION CHANGED ===');
    console.log('Selected user IDs:', userIds);
    console.log('User IDs type:', typeof userIds);
    console.log('User IDs length:', userIds?.length);
    console.log('All users data:', allUsers?.data);
    
    setSelectedUserIds(userIds);
    setShowAllUsers(userIds.length === 0);
    
    console.log('About to fetch data for user IDs:', userIds);
    
    // SECURITY FIX: Only fetch data for selected users, never show all users' data
    if (userIds.length === 0) {
      // Clear all data when no users are selected - SECURITY MEASURE
      console.log('No users selected - clearing all data for security');
      setAllDocuments({ data: { documents: [] } });
      setAllTaskAssignments({ data: { taskAssignments: [] } });
      setDocumentTotalCount(0);
      setTaskTotalCount(0);
    } else {
      // Show selected users' data only
      console.log('Fetching data for selected users:', userIds);
      fetchUserData(userIds);
    }
  };

  const handleShowAllUsers = () => {
    // SECURITY FIX: Remove this function as it exposes all users' data
    // Limited admins should only see data for specifically selected users
    console.warn('handleShowAllUsers called - this function has been disabled for security reasons');
    setSelectedUserIds([]);
    setShowAllUsers(true);
    // Clear all data instead of showing all users' data
    setAllDocuments({ data: { documents: [] } });
    setAllTaskAssignments({ data: { taskAssignments: [] } });
    setDocumentTotalCount(0);
    setTaskTotalCount(0);
  };

  const handleViewDocumentFlow = (document) => {
    navigate(`/limited-admin/document-flow/${document.id}`);
  };

  // Task handler functions
  const handleViewTask = (task) => {
    setSelectedTask(task);
    setTaskDetailOpen(true);
  };
  
  const handleOpenRejectDialog = (task) => {
    setSelectedTask(task);
    setRejectDialogOpen(true);
  };
  
  const handleOpenApproveDialog = (task) => {
    setSelectedTask(task);
    setApproveDialogOpen(true);
  };
  
  const handleForwardTask = async (task, forwardToId, reason, departmentId, metadata) => {
    try {
      // Create the forward request data
      const forwardData = {
        forward_to_id: forwardToId,
        department_id: departmentId,
        reason: reason
      };
      
      // Use the dedicated forward API endpoint
      const response = await apiClient.post(
        `/task-assignments/${task.id}/forward`,
        forwardData
      );
      
      // Create an activity record for the forwarding
      await createActivity({
        document_id: task.document_id || task.document?.id,
        user_id: user.id,
        activity_type: 'FORWARDED',
        description: `${user.full_name || user.username} forwarded the task to ${forwardToId ? 'another user' : departmentId ? 'a department' : 'unknown recipient'}`,
        step_id: task.workflow_step_id,
        metadata: {
          task_id: task.id,
          document_id: task.document_id || task.document?.id,
          user_id: user.id,
          user_name: user.full_name || user.username,
          action: 'FORWARDED',
          reason: reason,
          from: user.full_name || user.username,
          to: forwardToId ? 'another user' : departmentId ? 'a department' : 'unknown recipient'
        }
      });
      
      // Refresh tasks after forwarding
      if (allTaskAssignments?.data?.taskAssignments) {
        fetchAssignedUsers();
      }
      setTaskDetailOpen(false);
    } catch (err) {
      console.error('Error forwarding task:', err);
    }
  };
  
  const handleCloseTaskDetail = () => {
    setTaskDetailOpen(false);
    setSelectedTask(null);
  };

  const handleExportData = (dataType, format = 'pdf') => {
    console.log('Export data type:', dataType, 'Format:', format);
    
    if (dataType === 'documents') {
      const filteredDocuments = getFilteredDocuments();
      // Pass all task assignments and helper functions to PDF generation
      if (format === 'excel') {
        generateDocumentsExcel(filteredDocuments, getSelectedUsers(), t);
      } else {
        generateDocumentsPDF(
          filteredDocuments, 
          selectedUserIds, 
          getSelectedUsers(), 
          isRTL, 
          t,
          allTaskAssignments?.data?.taskAssignments || [],
          getTaskStatusLabel,
          getTaskStatusColor
        );
      }
    } else if (dataType === 'tasks') {
      const filteredTasks = getFilteredTasks();
      if (format === 'excel') {
        generateTasksExcel(filteredTasks, getSelectedUsers(), t, getTaskStatusLabel);
      } else {
        generateTasksPDF(filteredTasks, selectedUserIds, getSelectedUsers(), taskViewMode, isRTL, t, getTaskStatusLabel);
      }
    }
  };

  // Helper functions to filter data based on selected users
  const getFilteredDocuments = () => {
    if (!allDocuments?.data?.documents) {
      return [];
    }
    
    let filtered = allDocuments.data.documents;
    
    // Apply search filter
    if (documentSearch.trim()) {
      const searchTerm = documentSearch.toLowerCase();
      filtered = filtered.filter(doc => 
        (doc.title || '').toLowerCase().includes(searchTerm) ||
        (doc.reference_number || '').toLowerCase().includes(searchTerm) ||
        (doc.creator?.full_name || '').toLowerCase().includes(searchTerm) ||
        (doc.creator?.username || '').toLowerCase().includes(searchTerm) ||
        (doc.description || '').toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply status filter - check both document status and task assignment status
    if (documentStatusFilter) {
      filtered = filtered.filter(doc => {
        // Get the effective status (task status if exists, otherwise document status)
        const taskStatus = getDocumentTaskStatus(doc.id);
        const effectiveStatus = taskStatus || doc.status;
        
        // Match against the effective status
        return effectiveStatus === documentStatusFilter;
      });
    }
    
    // Apply type filter
    if (documentTypeFilter) {
      filtered = filtered.filter(doc => doc.type === documentTypeFilter);
    }
    
    return filtered;
  };

  const getFilteredTasks = () => {
    if (!allTaskAssignments?.data?.taskAssignments) return [];
    
    const allTasks = allTaskAssignments.data.taskAssignments;
    
    console.log('=== getFilteredTasks Debug ===');
    console.log('taskViewMode:', taskViewMode);
    console.log('selectedStatus:', selectedStatus);
    console.log('allTasks count:', allTasks.length);
    console.log('selectedUserIds:', selectedUserIds);
    console.log('user?.id:', user?.id);
    
    let filteredTasks = [];
    
    // Filter by user selection and view mode
    if (selectedUserIds.length === 0) {
      // If no users selected, show no tasks
      filteredTasks = [];
      console.log('No users selected - showing no tasks');
    } else if (taskViewMode === 'assigned-by-me') {
      // Show tasks assigned by the current user (limited admin) to selected users
      console.log('=== ASSIGNED-BY-ME DEBUG ===');
      console.log('Current user ID:', user?.id);
      console.log('Selected user IDs:', selectedUserIds);
      console.log('All tasks count:', allTasks.length);
      
      // First, let's see all tasks assigned by current user (regardless of assignee)
      const tasksAssignedByMe = allTasks.filter(task => task.assigner_id === user?.id);
      console.log('Tasks assigned by current user (any assignee):', tasksAssignedByMe.length);
      console.log('Tasks assigned by current user:', tasksAssignedByMe.map(t => ({
        id: t.id,
        assigner_id: t.assigner_id,
        assignee_id: t.assignee_id,
        status: t.status
      })));
      
      // For assigned-by-me mode, show tasks assigned BY the selected users
      filteredTasks = allTasks.filter(task => 
        selectedUserIds.includes(task.assigner_id)
      );
      console.log('assigned-by-me filtered tasks (limited admin assigned to selected users):', filteredTasks.length);
      console.log('Filtered tasks:', filteredTasks.map(t => ({
        id: t.id,
        assigner_id: t.assigner_id,
        assignee_id: t.assignee_id,
        status: t.status
      })));
    } else if (taskViewMode === 'assigned-to-me') {
      // Show tasks assigned TO selected users (they are the assignees)
      filteredTasks = allTasks.filter(task => 
        selectedUserIds.includes(task.assignee_id)
      );
      console.log('assigned-to-me filtered tasks (tasks assigned to selected users):', filteredTasks.length);
    } else if (taskViewMode === 'all-related') {
      // Show ALL tasks related to selected users (both assigned to them and assigned by them)
      filteredTasks = allTasks.filter(task => 
        selectedUserIds.includes(task.assignee_id) || selectedUserIds.includes(task.assigner_id)
      );
      console.log('all-related filtered tasks (all tasks related to selected users):', filteredTasks.length);
    }
    
    // Apply ownership rules for specific statuses
    filteredTasks = filteredTasks.filter(task => {
      if (task.status === 'COMPLETED') {
        // Completed tasks should only be shown to the original assigner (owner)
        if (taskViewMode === 'assigned-by-me') {
          return selectedUserIds.includes(task.assigner_id);
        }
        return selectedUserIds.includes(task.assigner_id);
      } else if (task.status === 'FORWARDED') {
        // Forwarded tasks should only be shown to the forwarder
        if (taskViewMode === 'assigned-by-me') {
          return selectedUserIds.includes(task.forwarded_by_id);
        }
        return selectedUserIds.includes(task.forwarded_by_id);
      }
      // For other statuses, use normal filtering
      return true;
    });
    console.log('Tasks after ownership rules:', filteredTasks.length);
    
    // Apply status filter - prioritize taskStatusFilter over selectedStatus
    const statusToFilter = taskStatusFilter || (selectedStatus !== 'all' ? selectedStatus : null);
    
    if (statusToFilter) {
      if (statusToFilter === 'IN_PROGRESS') {
        // For IN_PROGRESS, include both IN_PROGRESS and ACCEPTED statuses
        filteredTasks = filteredTasks.filter(task => 
          task.status === 'IN_PROGRESS' || task.status === 'ACCEPTED'
        );
      } else {
        filteredTasks = filteredTasks.filter(task => task.status === statusToFilter);
      }
    }
    
    // Apply search filter
    if (taskSearch.trim()) {
      const searchTerm = taskSearch.toLowerCase();
      filteredTasks = filteredTasks.filter(task => 
        (task.document?.title || '').toLowerCase().includes(searchTerm) ||
        (task.document?.reference_number || '').toLowerCase().includes(searchTerm) ||
        (task.assignee?.full_name || '').toLowerCase().includes(searchTerm) ||
        (task.assignee?.username || '').toLowerCase().includes(searchTerm) ||
        (task.assigner?.full_name || '').toLowerCase().includes(searchTerm) ||
        (task.assigner?.username || '').toLowerCase().includes(searchTerm) ||
        (task.notes || '').toLowerCase().includes(searchTerm)
      );
    }
    
    return filteredTasks;
  };

  // Get all tasks for the "All Tasks" card count
  const getAllTasks = () => {
    if (!allTaskAssignments?.data?.taskAssignments) return [];
    return allTaskAssignments.data.taskAssignments;
  };


  const getSelectedUsers = () => {
    if (!allUsers?.data || selectedUserIds.length === 0) return [];
    return allUsers.data.filter(user => selectedUserIds.includes(user.id));
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'primary';
    }
  };

  const getLinearProgressColor = (status) => {
    switch (status) {
      case 'healthy': return 'primary';
      case 'warning': return 'secondary';
      case 'critical': return 'error';
      default: return 'primary';
    }
  };

  // Helper to get latest task assignment status for a document
  const getDocumentTaskStatus = (docId) => {
    // Use taskAssignmentsForDocuments directly (like DocumentList) for documents tab
    // This ensures we get the most up-to-date status from the by-document-ids endpoint
    if (taskAssignmentsForDocuments && Array.isArray(taskAssignmentsForDocuments)) {
      const assignment = taskAssignmentsForDocuments.find(t => t.document_id === docId);
      if (assignment) {
        console.log(`Document ${docId} assignment:`, assignment);
        console.log(`Document ${docId} status:`, assignment?.effective_status || assignment?.status);
        // Use effective_status if available, otherwise fall back to status
        return assignment?.effective_status || assignment?.status || null;
      }
    }
    
    // Fallback to allTaskAssignments for other use cases (e.g., tasks tab)
    if (!allTaskAssignments?.data?.taskAssignments || !Array.isArray(allTaskAssignments.data.taskAssignments)) {
      console.log(`No task assignments data for document ${docId}`);
      return null;
    }
    const assignment = allTaskAssignments.data.taskAssignments.find(t => t.document_id === docId);
    // Use effective_status if available, otherwise fall back to status
    return assignment?.effective_status || assignment?.status || null;
  };

  // Helper to get status color for task assignment statuses
  const getTaskStatusColor = (status) => ({
    ASSIGNED: 'default',
    ACCEPTED: 'info',
    RECEIVED: 'info',
    IN_PROGRESS: 'primary',
    COMPLETED: 'success',
    REJECTED: 'error',
    REJECTED_RECEIPT: 'error',
    FORWARDED: 'warning',
    SKIPPED: 'secondary'
  }[status] || 'default');

  // Helper to get status label for task assignment statuses
  const getTaskStatusLabel = (status) => {
    const key = `limitedadmin:${status.toLowerCase()}`;
    const translated = t(key, status);
    return translated;
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />;
      case 'warning': return <WarningIcon />;
      case 'critical': return <ErrorIcon />;
      default: return <ErrorIcon />;
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('limitedadmin:error_loading_dashboard')}
        </Alert>
        <Alert severity="info" sx={{ mb: 2 }}>
          Error details: {error.message || 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('limitedadmin:limited_admin_dashboard')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUserProfile}
            color="secondary"
          >
            {t('limitedadmin:refresh_profile')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchAssignedUsers()}
          >
            {t('common:refresh')}
          </Button>
      </Box>
      </Box>



      {/* Main Content Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={t('limitedadmin:documents')} />
            <Tab label={t('limitedadmin:tasks')} />
          </Tabs>
        </Box>
        
        <CardContent>
          {/* Limited Admin Info */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('limitedadmin:assigned_users_overview')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('limitedadmin:assigned_users_description')}
              </Typography>
              
              {/* User Selection Dropdown */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                    {t('limitedadmin:users_available', '{{count}} users available', { count: allUsers?.data?.length || 0 })}
                  </Typography>
                </Box>
                <RTLAutocomplete
                  fullWidth
                  {...getRTLProps('autocomplete')}
                  multiple
                  disablePortal
                  disableCloseOnSelect
                  popupIcon={null}
                  options={allUsers?.data || []}
                  getOptionLabel={(option) => option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
                  value={allUsers?.data?.filter(user => selectedUserIds.includes(user.id)) || []}
                  onChange={(event, newValue) => handleUserSelection(newValue.map(user => user.id))}
                  filterOptions={(options, { inputValue }) => {
                    const searchTerm = inputValue.toLowerCase();
                    return options.filter(option => {
                      const fullName = (option.full_name || '').toLowerCase();
                      const username = (option.username || '').toLowerCase();
                      const email = (option.email || '').toLowerCase();
                      const department = (option.department?.name || '').toLowerCase();
                      
                      return fullName.includes(searchTerm) || 
                             username.includes(searchTerm) || 
                             email.includes(searchTerm) ||
                             department.includes(searchTerm);
                    });
                  }}
                  renderInput={(params) => (
                    <RTLTextField
                      {...params}
                      label={t('limitedadmin:select_users_to_view_data')}
                      placeholder={t('limitedadmin:search_users', 'Search by name, username, email, or department...')}
                      InputProps={{
                        ...params.InputProps,
                        // Remove the dropdown icon for multiselect
                        endAdornment: null,
                      }}
                      {...getRTLProps('textfield')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          // Ensure no dropdown icon appears
                          '& .MuiAutocomplete-popupIndicator': {
                            display: 'none !important',
                          },
                        },
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) => 
                    value.map((option, index) => {
                      console.log('Rendering tag for user:', option);
                      return (
                        <Chip
                          key={option.id}
                          label={option.full_name || option.username}
                          size="small"
                          sx={{
                            backgroundColor: 'primary.50',
                            color: 'primary.main',
                            border: '1px solid',
                            borderColor: 'primary.200',
                            '& .MuiChip-deleteIcon': {
                              color: 'primary.main',
                              '&:hover': {
                                color: 'primary.dark',
                              },
                            },
                          }}
                          {...getTagProps({ index })}
                        />
                      );
                    })
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ p: 2, borderBottom: '1px solid #f0f0f0', '&:hover': { bgcolor: '#f8f9fa' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            bgcolor: 'primary.main', 
                            flexShrink: 0,
                            fontSize: '0.875rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {(option.full_name || option.username || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, textAlign: isRTL ? 'right' : 'left', minWidth: 0 }}>
                          <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }} noWrap>
                            {option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {option.email}
                          </Typography>
                          {option.department?.name && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                              {option.department.name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  noOptionsText={t('limitedadmin:no_users_found', 'No users found')}
                  loadingText={t('common:loading_users', 'Loading users...')}
                  sx={{
                    '& .MuiAutocomplete-paper': {
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      border: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                />
                
                {selectedUserIds.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('limitedadmin:showing_data_for')}: 
                      {selectedUserIds.length === allUsers?.data?.length ? (
                        <strong> {t('limitedadmin:all_users')}</strong>
                      ) : (
                        <strong> {getSelectedUsers().map(user => user.full_name || user.username).join(', ')}</strong>
                      )}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => handleUserSelection([])}
                      startIcon={<ClearIcon />}
                    >
                      {t('limitedadmin:clear_selection')}
                    </Button>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={t('limitedadmin:assigned_users_count', { count: allUsers?.data?.length || 0 })}
                  color="primary"
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  onClick={() => fetchAssignedUsers()}
                  startIcon={<RefreshIcon />}
                  size="small"
                >
                  {t('common:refresh')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    console.log('Manual data fetch triggered');
                    console.log('Current selectedUserIds:', selectedUserIds);
                    console.log('Current allUsers:', allUsers);
                    if (selectedUserIds.length > 0) {
                      fetchUserData(selectedUserIds);
                    } else {
                      // SECURITY FIX: Don't load all users' data when none are selected
                      console.log('SECURITY: No users selected - cannot load data');
                      setAllDocuments({ data: { documents: [] } });
                      setAllTaskAssignments({ data: { taskAssignments: [] } });
                      setDocumentTotalCount(0);
                      setTaskTotalCount(0);
                    }
                  }}
                  size="small"
                  color="secondary"
                >
                  {t('limitedadmin:load_data')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Documents Tab */}
          {activeTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {showAllUsers ? t('limitedadmin:assigned_users_documents') : t('limitedadmin:selected_user_documents')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<GetAppIcon />}
                    onClick={() => handleExportData('documents', 'excel')}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      px: 2.5,
                      py: 1,
                      backgroundColor: '#1b5e20',
                      color: 'white',
                      boxShadow: 'none',
                      border: '1px solid #1b5e20',
                      '&:hover': {
                        backgroundColor: '#2e7d32',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    {t('common:download')} {t('common:documents')} (Excel)
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => handleExportData('documents', 'pdf')}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      px: 2.5,
                      py: 1,
                      border: '1px solid',
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      backgroundColor: '#fff',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#c62828',
                        color: '#c62828',
                        backgroundColor: '#ffebee',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    {t('common:download')} {t('common:documents')} (PDF)
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <FilterListIcon sx={{ mr: 0.5 }} /> {t('common:filter')}
                    {(documentStatusFilter || documentTypeFilter) && (
                      <Chip 
                        label={[documentStatusFilter, documentTypeFilter].filter(Boolean).length} 
                        size="small" 
                        color="primary" 
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Button>
                </Box>
              </Box>

              {/* Search and Filters - Expandable */}
              <Box sx={{ mb: 3 }}>
                {showFilters && (
                  <>
                    {/* Active Filters Display */}
                    {(documentSearch || documentStatusFilter || documentTypeFilter) && (
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                          {t('common:active_filters', 'Active Filters')}:
                        </Typography>
                        {documentSearch && (
                          <Chip
                            label={`${t('common:search')}: "${documentSearch}"`}
                            size="small"
                            onDelete={() => setDocumentSearch('')}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {documentStatusFilter && (
                          <Chip
                            label={`${t('limitedadmin:document_status')}: ${getTaskStatusLabel(documentStatusFilter) || t(`limitedadmin:${documentStatusFilter.toLowerCase()}`) || documentStatusFilter}`}
                            size="small"
                            onDelete={() => setDocumentStatusFilter('')}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {documentTypeFilter && (
                          <Chip
                            label={`${t('limitedadmin:document_type')}: ${documentTypeFilter}`}
                            size="small"
                            onDelete={() => setDocumentTypeFilter('')}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        <Button
                          size="small"
                          onClick={() => {
                            setDocumentSearch('');
                            setDocumentStatusFilter('');
                            setDocumentTypeFilter('');
                          }}
                          sx={{ ml: 'auto' }}
                        >
                          {t('common:clear_all')}
                        </Button>
                      </Box>
                    )}
                    
                    {/* Search and Filters Panel */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      mb: 2, 
                      flexWrap: 'wrap', 
                      alignItems: 'flex-start'
                    }}>
                <RTLTextField
                  fullWidth
                  size="small"
                  placeholder={t('limitedadmin:search_documents', 'Search documents by title, reference, or creator...')}
                  value={documentSearch}
                  onChange={(e) => setDocumentSearch(e.target.value)}
                  {...getRTLProps('textfield')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  }}
                        sx={{ flexGrow: 1, minWidth: 300 }}
                />
                    </Box>
                
                    {/* Filters Panel */}
                <Box sx={{ 
                      p: 2.5,
                      bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                        {t('common:filter_options', 'Filter Options')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{t('limitedadmin:document_status')}</InputLabel>
                    <Select
                      value={documentStatusFilter}
                      onChange={(e) => setDocumentStatusFilter(e.target.value)}
                      label={t('limitedadmin:document_status')}
                    >
                      <MenuItem value="">{t('common:all')}</MenuItem>
                      <MenuItem value="DRAFT">{t('limitedadmin:draft')}</MenuItem>
                      <MenuItem value="REJECTED">{t('limitedadmin:rejected')}</MenuItem>
                            <MenuItem value="COMPLETED">{t('limitedadmin:completed')}</MenuItem>
                            {/* Task assignment statuses */}
                            <MenuItem value="ASSIGNED">{t('limitedadmin:assigned')}</MenuItem>
                            <MenuItem value="IN_PROGRESS">{t('limitedadmin:in_progress')}</MenuItem>
                    </Select>
                  </FormControl>
                  
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{t('limitedadmin:document_type')}</InputLabel>
                    <Select
                      value={documentTypeFilter}
                      onChange={(e) => setDocumentTypeFilter(e.target.value)}
                      label={t('limitedadmin:document_type')}
                    >
                      <MenuItem value="">{t('common:all')}</MenuItem>
                      <MenuItem value="مکتوب">{t('limitedadmin:letter')}</MenuItem>
                            <MenuItem value="استعلام">{t('limitedadmin:inquiry', 'استعلام')}</MenuItem>
                            <MenuItem value="پیشنهاد">{t('limitedadmin:proposal', 'پیشنهاد')}</MenuItem>
                            <MenuItem value="گزارش">{t('limitedadmin:report')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                    </Box>
                  </>
                )}
              </Box>
              {dataLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (() => {
                const filteredDocuments = getFilteredDocuments();
                return (
                  <>
                <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                        <TableCell sx={{ textAlign: 'right' }}>#{t('common:number')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:title')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:reference')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:type')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:status')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:creator')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:created_at')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{t('common:actions')}</TableCell>
                </TableRow>
              </TableHead>
                    <TableBody>
                      {filteredDocuments.length > 0 ? (
                          filteredDocuments.map((document, index) => (
                        <TableRow 
                          key={document.id} 
                          hover
                        >
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {(documentPage - 1) * documentPageSize + index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">
                              {document.title}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">
                              {document.reference_number}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Chip
                              label={document.type}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            {(() => {
                              const taskStatus = getDocumentTaskStatus(document.id);
                              if (!taskStatus) {
                                // Fallback to document status if no task assignment
                                return (
                                  <Chip
                                    label={t(`limitedadmin:${document.status.toLowerCase()}`) || document.status}
                                    size="small"
                                    color="info"
                                  />
                                );
                              }
                              
                              // Only use valid task assignment statuses
                              const validTaskStatuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'FORWARDED', 'ACCEPTED', 'RECEIVED', 'REJECTED_RECEIPT'];
                              if (!validTaskStatuses.includes(taskStatus)) {
                                return (
                                  <Chip
                                    label={t(`limitedadmin:${document.status.toLowerCase()}`) || document.status}
                                    size="small"
                                    color="info"
                                  />
                                );
                              }
                              
                              return (
                                <Chip
                                  label={getTaskStatusLabel(taskStatus)}
                                  size="small"
                                  color={getTaskStatusColor(taskStatus)}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">
                              {document.creator?.full_name || document.creator?.username || t('limitedadmin:unknown_user')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Typography variant="body2">
                              {document.created_at ? format(new Date(document.created_at), 'dd/MM/yyyy') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Tooltip title={t('limitedadmin:view_document_flow')}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewDocumentFlow(document);
                                  }}
                                  color="primary"
                                >
                                  <TimelineIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                    </TableCell>
                  </TableRow>
                        ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {selectedUserIds.length === 0 ? t('limitedadmin:select_users_to_view_documents') : t('limitedadmin:no_documents_found')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                    </TableBody>
                  </Table>
                </TableContainer>
              
              {/* Documents Pagination */}
              {filteredDocuments.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('common:rows_per_page')}:
                    </Typography>
                                          <Select
                        value={documentPageSize}
                        onChange={(e) => setDocumentPageSize(e.target.value)}
                        size="small"
                        sx={{ minWidth: 70 }}
                      >
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={documentTotalCount}>All</MenuItem>
                      </Select>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('common:showing_results', 'Showing {{start}} to {{end}} of {{total}} results', { 
                      start: ((documentPage - 1) * documentPageSize) + 1, 
                      end: Math.min(documentPage * documentPageSize, documentTotalCount), 
                      total: documentTotalCount 
                    })}
                  </Typography>
                  <Pagination
                    count={Math.ceil(documentTotalCount / documentPageSize)}
                    page={documentPage}
                    onChange={(e, value) => setDocumentPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
                  </>
                );
              })()}
            </Box>
          )}

          {/* Tasks Tab */}
          {activeTab === 1 && (
            <Box>
              {/* Dynamic Task Status Overview Cards */}
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={2}>
                  {getStatusCardsConfig().map((card) => {
                    const IconComponent = card.icon;
                    const isSelected = selectedStatus === card.status;
                    
                    return (
                      <Grid item xs={12} sm={6} md={2} key={card.key}>
                        <Card 
                          sx={{ 
                            borderRadius: 3, 
                            boxShadow: isSelected ? '0 8px 24px rgba(25, 118, 210, 0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                            border: '2px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            backgroundColor: isSelected ? 'primary.50' : card.bgColor,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                              borderColor: isSelected ? 'primary.main' : 'primary.light'
                            }
                          }}
                          onClick={() => handleStatusClick(card.status)}
                        >
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Box sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              bgcolor: isSelected ? 'primary.main' : card.bgColor, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 1
                            }}>
                              <IconComponent sx={{ 
                                fontSize: 20, 
                                color: isSelected ? 'white' : `${card.color}.main` 
                              }} />
                            </Box>
                            <Typography 
                              variant="h6" 
                              fontWeight={700} 
                              color={isSelected ? 'primary.main' : 'text.primary'} 
                              gutterBottom
                            >
                              {card.getCount()}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                            >
                              {card.label}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {/* Task View Mode Switcher */}
              <Box sx={{ mb: 3 }}>
                <Card sx={{ 
                  borderRadius: 2, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexDirection: 'row'
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('limitedadmin:task_view_mode')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant={taskViewMode === 'all-related' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setTaskViewMode('all-related')}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            py: 1
                          }}
                        >
                          {t('limitedadmin:all_related_tasks')}
                        </Button>
                        <Button
                          variant={taskViewMode === 'assigned-to-me' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setTaskViewMode('assigned-to-me')}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            py: 1
                          }}
                        >
                          {t('limitedadmin:assigned_to_me')}
                        </Button>
                        <Button
                          variant={taskViewMode === 'assigned-by-me' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setTaskViewMode('assigned-by-me')}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                            py: 1
                          }}
                        >
                          {t('limitedadmin:assigned_by_me')}
                        </Button>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {taskViewMode === 'all-related' 
                        ? t('limitedadmin:view_all_tasks_related_to_selected_users')
                        : taskViewMode === 'assigned-to-me' 
                          ? t('limitedadmin:view_tasks_assigned_to_selected_users')
                          : t('limitedadmin:view_tasks_assigned_by_me')
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Task List Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {taskViewMode === 'all-related' 
                    ? t('limitedadmin:all_related_tasks')
                    : taskViewMode === 'assigned-by-me' 
                      ? t('limitedadmin:tasks_assigned_by_me')
                      : (showAllUsers ? t('limitedadmin:assigned_users_tasks') : t('limitedadmin:selected_user_tasks'))
                  }
                  {selectedStatus !== 'all' && (
                    <Chip 
                      label={getStatusCardsConfig().find(card => card.status === selectedStatus)?.label || selectedStatus}
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1 }}
                      onDelete={() => setSelectedStatus('all')}
                    />
                  )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<GetAppIcon />}
                    onClick={() => handleExportData('tasks', 'excel')}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      px: 2.5,
                      py: 1,
                      backgroundColor: '#1b5e20',
                      color: 'white',
                      boxShadow: 'none',
                      border: '1px solid #1b5e20',
                      '&:hover': {
                        backgroundColor: '#2e7d32',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    {t('common:download')} {t('my_assignments:tasks')} (Excel)
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => handleExportData('tasks', 'pdf')}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      px: 2.5,
                      py: 1,
                      border: '1px solid',
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      backgroundColor: '#fff',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#c62828',
                        color: '#c62828',
                        backgroundColor: '#ffebee',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    {t('common:download')} {t('my_assignments:tasks')} (PDF)
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <FilterListIcon sx={{ mr: 0.5 }} /> {t('common:filter')}
                    {(taskStatusFilter || selectedStatus !== 'all') && (
                      <Chip 
                        label={1} 
                        size="small" 
                        color="primary" 
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Button>
                </Box>
              </Box>

              {/* Task Search and Filters - Expandable */}
              <Box sx={{ mb: 3 }}>
                {showFilters && (
                  <>
                    {/* Active Filters Display */}
                    {(taskSearch || taskStatusFilter || selectedStatus !== 'all') && (
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                          {t('common:active_filters', 'Active Filters')}:
                        </Typography>
                        {taskSearch && (
                          <Chip
                            label={`${t('common:search')}: "${taskSearch}"`}
                            size="small"
                            onDelete={() => setTaskSearch('')}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {(taskStatusFilter || selectedStatus !== 'all') && (
                          <Chip
                            label={`${t('limitedadmin:task_status')}: ${getTaskStatusLabel(taskStatusFilter || selectedStatus)}`}
                            size="small"
                            onDelete={() => {
                              setTaskStatusFilter('');
                              setSelectedStatus('all');
                            }}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        <Button
                          size="small"
                          onClick={() => {
                            setTaskSearch('');
                            setTaskStatusFilter('');
                            setSelectedStatus('all');
                          }}
                          sx={{ ml: 'auto' }}
                        >
                          {t('common:clear_all')}
                        </Button>
                      </Box>
                    )}
                    
                    {/* Search and Filters Panel */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      mb: 2, 
                      flexWrap: 'wrap', 
                      alignItems: 'flex-start'
                    }}>
                <RTLTextField
                  fullWidth
                  size="small"
                  placeholder={t('limitedadmin:search_tasks', 'Search tasks by document title, assignee, or notes...')}
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                  {...getRTLProps('textfield')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  }}
                        sx={{ flexGrow: 1, minWidth: 300 }}
                />
                    </Box>
                
                    {/* Filters Panel */}
                <Box sx={{ 
                      p: 2.5,
                      bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                        {t('common:filter_options', 'Filter Options')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{t('limitedadmin:task_status')}</InputLabel>
                    <Select
                            value={taskStatusFilter || (selectedStatus !== 'all' ? selectedStatus : '')}
                            onChange={(e) => {
                              const value = e.target.value;
                              setTaskStatusFilter(value);
                              setSelectedStatus(value || 'all');
                            }}
                      label={t('limitedadmin:task_status')}
                    >
                      <MenuItem value="">{t('common:all')}</MenuItem>
                      <MenuItem value="ASSIGNED">{t('limitedadmin:assigned')}</MenuItem>
                      <MenuItem value="IN_PROGRESS">{t('limitedadmin:in_progress')}</MenuItem>
                      <MenuItem value="COMPLETED">{t('limitedadmin:completed')}</MenuItem>
                      <MenuItem value="REJECTED">{t('limitedadmin:rejected')}</MenuItem>
                      <MenuItem value="FORWARDED">{t('limitedadmin:forwarded')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        {t('limitedadmin:filter_note', 'Tip: You can also filter by clicking on the status cards above')}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
              {dataLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
          </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3 
                }}>
                      {(() => {
                        const filteredTasks = getFilteredTasks();
                        return filteredTasks.length > 0 ? (
                          filteredTasks.map((task) => (
                        <TaskCard 
                          key={task.id} 
                          task={task}
                          viewMode={taskViewMode}
                          onView={handleViewTask}
                          onAccept={handleTaskAccept}
                          onReject={handleOpenRejectDialog}
                          onApprove={handleOpenApproveDialog}
                          onForward={handleForwardTask}
                          t={t}
                        />
                  ))
                        ) : (
                      <Paper 
                        sx={{ 
                          p: 6, 
                          textAlign: 'center', 
                          borderRadius: 3,
                          backgroundColor: 'background.paper',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ 
                          width: 80, 
                          height: 80, 
                          borderRadius: '50%', 
                          bgcolor: 'grey.100', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3
                        }}>
                          <TaskIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                        </Box>
                        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                          {(() => {
                            if (selectedUserIds.length === 0) {
                              return t('limitedadmin:select_users_to_view_tasks');
                            } else if (taskViewMode === 'all-related') {
                              return t('limitedadmin:no_related_tasks_found');
                            } else if (taskViewMode === 'assigned-by-me') {
                              return t('limitedadmin:no_tasks_assigned_by_you');
                            } else {
                              return t('limitedadmin:no_tasks_found');
                            }
                          })()}
                        </Typography>
                              <Typography variant="body2" color="text.secondary">
                          {selectedUserIds.length === 0 
                            ? t('limitedadmin:try_selecting_different_users')
                            : taskViewMode === 'all-related'
                              ? t('limitedadmin:try_selecting_different_users_or_view_mode')
                              : taskViewMode === 'assigned-by-me' 
                                ? t('limitedadmin:try_assigning_some_tasks')
                                : t('limitedadmin:try_selecting_different_users')
                          }
                              </Typography>
                      </Paper>
                        );
                      })()}
                </Box>
              )}

              {/* Tasks Pagination */}
              {(() => {
                const filteredTasks = getFilteredTasks();
                return filteredTasks.length > 0 && taskTotalCount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('common:rows_per_page')}:
                      </Typography>
                      <Select
                        value={taskPageSize}
                        onChange={(e) => setTaskPageSize(e.target.value)}
                        size="small"
                        sx={{ minWidth: 70 }}
                      >
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={taskTotalCount}>All</MenuItem>
                      </Select>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('common:showing_results', 'Showing {{start}} to {{end}} of {{total}} results', { 
                        start: ((taskPage - 1) * taskPageSize) + 1, 
                        end: Math.min(taskPage * taskPageSize, taskTotalCount), 
                        total: taskTotalCount 
                      })}
                    </Typography>
                    <Pagination
                      count={Math.ceil(taskTotalCount / taskPageSize)}
                      page={taskPage}
                      onChange={(e, value) => setTaskPage(value)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                );
              })()}
            </Box>
          )}


        </CardContent>
      </Card>

      {/* Task Detail Dialog */}
      <TaskDetailDialog 
        task={selectedTask}
        open={taskDetailOpen}
        onClose={handleCloseTaskDetail}
        onSuccess={(task, action) => {
          // Handle all success cases
          switch(action) {
            case 'received': 
              fetchAssignedUsers();
              break;
            case 'completed': 
              // Completion is handled by TaskDetailDialogV2 internally
              break;
            case 'rejected': 
              // Rejection is handled by TaskDetailDialogV2 internally
              break;
            case 'forwarded': 
              // Forwarding is handled by TaskDetailDialogV2 internally
              break;
          }
        }}
      />
      
      {/* Task Action Dialogs */}
      <RejectDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={(reason) => handleTaskReject(selectedTask, reason)}
        isRtl={isRTL}
      />
      
      <ApproveDialog 
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onApprove={(remarks, attachment) => handleTaskApprove(selectedTask, remarks, attachment)}
        isRtl={isRTL}
      />

    </Box>
  );
};

export default LimitedAdminDashboard;
